import {
  AccountsPayableCreditNotePermission,
  AccountsPayablePaymentPermission,
  AccountsReceivableCreditNotePermission,
  AccountsReceiveablePaymentPermission,
  BillPermission,
  CustomerPermission,
  InvoicePermission,
  NotePermission,
  OrderPermission,
  ProductPermission,
  PurchaseOrderPermission,
  type RBACPermission,
  SupplierPermission,
} from "~/shared/types/RBACPermission";
import type { EntityLinkKind, ReportFieldDefinition, ReportRow } from "~/utils/types/assistant-report-artifact";

export type EntityRecordScope = "module" | "customer" | "sales_order" | "purchase_order" | "invoice" | "credit_note" | "customer_payment";

interface EntityRecordDescriptor {
  permissions: RBACPermission[];
  modelIdKey: string;
  scope: EntityRecordScope;
  routeKind: "detail" | "list_query" | "payment_batch";
  routePattern: RegExp;
  href: (id: string, number?: string, routeId?: string) => string | null;
  proseHref?: (id: string) => string;
  label: string;
  idLabel: string;
  tagType: string;
  /** Plural noun for the Assistant's link guidance, which is generated from these descriptors. */
  linkNoun: string;
  /** A record whose page belongs to its parent (a supplier payment opens its batch) names the row key holding that id. */
  route?: { idKey: string; idLabel: string };
}

const listHref = (path: string, number: string | undefined): string | null =>
  number ? `${path}?query=${encodeURIComponent(number)}` : null;

/** Browser-safe declarations; executable record scope readers stay in the server's RBAC repository boundary. */
export const ENTITY_RECORD_DESCRIPTORS: Record<EntityLinkKind, EntityRecordDescriptor> = {
  note: {
    permissions: [NotePermission.view, NotePermission.viewSalesPerson],
    modelIdKey: "noteId",
    scope: "module",
    routeKind: "detail",
    routePattern: /^\/notes(?:\/|$)/i,
    href: id => `/notes/${id}`,
    proseHref: () => "/notes",
    label: "View Note",
    idLabel: "Note Id",
    tagType: "note",
    linkNoun: "notes",
  },
  customer: {
    permissions: [CustomerPermission.view, CustomerPermission.viewSalesPerson],
    modelIdKey: "customerId",
    scope: "customer",
    routeKind: "detail",
    routePattern: /^\/customers\/[a-f0-9-]{36}/i,
    href: id => `/customers/${id}`,
    label: "View Customer",
    idLabel: "Customer Id",
    tagType: "customer",
    linkNoun: "customers",
  },
  supplier: {
    permissions: [SupplierPermission.view],
    modelIdKey: "supplierId",
    scope: "module",
    routeKind: "detail",
    routePattern: /^\/suppliers\/[a-f0-9-]{36}/i,
    href: id => `/suppliers/${id}`,
    label: "View Supplier",
    idLabel: "Supplier Id",
    tagType: "supplier",
    linkNoun: "suppliers",
  },
  product: {
    permissions: [ProductPermission.view, ProductPermission.viewAll],
    modelIdKey: "productId",
    scope: "module",
    routeKind: "detail",
    routePattern: /^\/products\/[a-f0-9-]{36}/i,
    href: id => `/products/${id}`,
    label: "View Product",
    idLabel: "Product Id",
    tagType: "product",
    linkNoun: "products",
  },
  sales_order: {
    permissions: [OrderPermission.view, OrderPermission.viewAssigned, OrderPermission.viewSalesPerson],
    modelIdKey: "salesOrderId",
    scope: "sales_order",
    routeKind: "detail",
    routePattern: /^\/orders\/[a-f0-9-]{36}/i,
    href: id => `/orders/${id}`,
    label: "View Sales Order",
    idLabel: "Sales Order Id",
    tagType: "order",
    linkNoun: "sales orders",
  },
  purchase_order: {
    permissions: [PurchaseOrderPermission.view],
    modelIdKey: "purchaseOrderId",
    scope: "purchase_order",
    routeKind: "detail",
    routePattern: /^\/purchase-orders\/[a-f0-9-]{36}/i,
    href: id => `/purchase-orders/${id}`,
    label: "View Purchase Order",
    idLabel: "Purchase Order Id",
    tagType: "purchaseorder",
    linkNoun: "purchase orders",
  },
  invoice: {
    permissions: [InvoicePermission.view],
    modelIdKey: "invoiceId",
    scope: "invoice",
    routeKind: "list_query",
    routePattern: /^\/accounting\/accounts-receivable\/invoices\?query=.+$/i,
    href: (_id, number) => listHref("/accounting/accounts-receivable/invoices", number),
    label: "View Invoice",
    idLabel: "Invoice Id",
    tagType: "invoice",
    linkNoun: "invoices",
  },
  credit_note: {
    permissions: [AccountsReceivableCreditNotePermission.view],
    modelIdKey: "creditNoteId",
    scope: "credit_note",
    routeKind: "list_query",
    routePattern: /^\/accounting\/accounts-receivable\/credit-notes\?query=.+$/i,
    href: (_id, number) => listHref("/accounting/accounts-receivable/credit-notes", number),
    label: "View Credit",
    idLabel: "Credit Id",
    tagType: "creditnote",
    linkNoun: "customer credits",
  },
  customer_payment: {
    permissions: [AccountsReceiveablePaymentPermission.view],
    modelIdKey: "customerPaymentId",
    scope: "customer_payment",
    routeKind: "list_query",
    routePattern: /^\/accounting\/accounts-receivable\/payments\?query=.+$/i,
    href: (_id, number) => listHref("/accounting/accounts-receivable/payments", number),
    label: "View Payment",
    idLabel: "Customer Payment Id",
    tagType: "customerpayment",
    linkNoun: "customer payments",
  },
  deposit: {
    permissions: [AccountsReceiveablePaymentPermission.view],
    modelIdKey: "depositId",
    scope: "module",
    routeKind: "detail",
    routePattern: /^\/accounting\/accounts-receivable\/deposits\/[a-f0-9-]{36}/i,
    href: id => `/accounting/accounts-receivable/deposits/${id}`,
    label: "View Deposit",
    idLabel: "Deposit Id",
    tagType: "deposit",
    linkNoun: "deposits",
  },
  bill: {
    permissions: [BillPermission.view],
    modelIdKey: "billId",
    scope: "module",
    routeKind: "detail",
    routePattern: /^\/accounting\/accounts-payable\/bills\/[a-f0-9-]{36}/i,
    href: id => `/accounting/accounts-payable/bills/${id}`,
    label: "View Bill",
    idLabel: "Bill Id",
    tagType: "bill",
    linkNoun: "bills",
  },
  supplier_credit: {
    permissions: [AccountsPayableCreditNotePermission.view],
    modelIdKey: "supplierCreditId",
    scope: "module",
    routeKind: "list_query",
    routePattern: /^\/accounting\/accounts-payable\/credit-notes\?query=.+$/i,
    href: (_id, number) => listHref("/accounting/accounts-payable/credit-notes", number),
    label: "View Supplier Credit",
    idLabel: "Supplier Credit Id",
    tagType: "suppliercredit",
    linkNoun: "supplier credits",
  },
  supplier_payment: {
    permissions: [AccountsPayablePaymentPermission.view],
    modelIdKey: "supplierPaymentId",
    scope: "module",
    routeKind: "payment_batch",
    routePattern: /^\/accounting\/accounts-payable\/payments\/[a-f0-9-]{36}/i,
    href: (_id, _number, routeId) => (routeId ? `/accounting/accounts-payable/payments/${routeId}` : null),
    route: { idKey: "batchId", idLabel: "Batch Id" },
    label: "View Payment",
    idLabel: "Supplier Payment Id",
    tagType: "supplierpayment",
    linkNoun: "supplier payments",
  },
};

/** Route templates in descriptor order: "{number}" is the record's number and "{batchId}" its payment batch id. */
export const ENTITY_LINK_ROUTES = Object.values(ENTITY_RECORD_DESCRIPTORS)
  .map(
    descriptor =>
      `${descriptor.linkNoun} ${decodeURIComponent(descriptor.href(`{${descriptor.modelIdKey}}`, "{number}", "{batchId}") ?? "")}`,
  )
  .join(", ");

export const isEntityLinkKind = (value: unknown): value is EntityLinkKind =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(ENTITY_RECORD_DESCRIPTORS, value);

const descriptorMap = <T>(project: (descriptor: EntityRecordDescriptor) => T): Record<EntityLinkKind, T> => {
  // Safe: the source is an exhaustive Record<EntityLinkKind, ...>; mapping preserves every key. Descriptor census tests cover it.
  return Object.fromEntries(Object.entries(ENTITY_RECORD_DESCRIPTORS).map(([key, descriptor]) => [key, project(descriptor)])) as Record<
    EntityLinkKind,
    T
  >;
};

export const ENTITY_LINK_PERMISSIONS = descriptorMap(descriptor => descriptor.permissions);
export const ENTITY_MODEL_ID_KEYS = descriptorMap(descriptor => descriptor.modelIdKey);
export const PER_RECORD_SCOPE_KINDS: readonly EntityLinkKind[] = Object.keys(ENTITY_RECORD_DESCRIPTORS)
  .filter(isEntityLinkKind)
  .filter(kind => ENTITY_RECORD_DESCRIPTORS[kind].scope !== "module");

export const entityLinkAllowed = (kind: unknown, hasPermission: (required: RBACPermission[]) => boolean): boolean =>
  isEntityLinkKind(kind) && hasPermission(ENTITY_RECORD_DESCRIPTORS[kind].permissions);

export const entityKindForTag = (tag: string): EntityLinkKind | null =>
  Object.keys(ENTITY_RECORD_DESCRIPTORS)
    .filter(isEntityLinkKind)
    .find(kind => ENTITY_RECORD_DESCRIPTORS[kind].tagType === tag.toLowerCase()) ?? null;

export const entityKindForHref = (href: string): EntityLinkKind | null =>
  Object.keys(ENTITY_RECORD_DESCRIPTORS)
    .filter(isEntityLinkKind)
    .find(kind => ENTITY_RECORD_DESCRIPTORS[kind].routePattern.test(href)) ?? null;

type EntityRef = NonNullable<ReportFieldDefinition["entityRef"]>;
export const modelIdKey = (entityRef: EntityRef, sharesKind = false): string =>
  entityRef.entity && !sharesKind ? ENTITY_MODEL_ID_KEYS[entityRef.entity] : entityRef.idKey;
export const entityDrillScopeKey = (idKey: string): string => `${idKey}ViewInScope`;
export const entityDrillViewInScopeForRow = (row: ReportRow, idKey: string): boolean => row[entityDrillScopeKey(idKey)] === true;
